import { useMemo, useRef } from 'react'
import { Label } from '../ui/label'
import { Input } from '../ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { Loader2, Save, Upload } from 'lucide-react'
import { Button } from '../ui/button'
import toast from 'react-hot-toast'
import {
  useGetCompanyQuery,
  useUpdateCompanyMutation,
} from '@/redux/service/datascope'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Skeleton } from '../ui/skeleton'

interface CompanySettingsData {
  name: string
  logo: string | File | null
  timezone: string
  symbol: string | null
  dailyHours: number
  workdays: string[]
}
const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const
type DayAbbreviation = (typeof DAYS_OF_WEEK)[number]

const INITIAL_COMPANY_SETTINGS: CompanySettingsData = {
  name: '',
  logo: null,
  timezone: 'UTC',
  symbol: null,
  dailyHours: 8,
  workdays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
}

const FULL_DAY_NAMES: Record<DayAbbreviation, string> = {
  Mon: 'Monday',
  Tue: 'Tuesday',
  Wed: 'Wednesday',
  Thu: 'Thursday',
  Fri: 'Friday',
  Sat: 'Saturday',
  Sun: 'Sunday',
}

const OrganizationDetails = () => {
  const { data: companyData, isLoading: isCompanyLoading } =
    useGetCompanyQuery()
  const [updateCompany, { isLoading: isUpdatingCompany }] =
    useUpdateCompanyMutation()

  const fileInputRef = useRef<HTMLInputElement>(null)

  const initialValues = useMemo(() => {
    if (companyData?.data) {
      const apiData = companyData.data
      return {
        name: apiData.name || '',
        logo: apiData.logo || null,
        timezone: apiData.timezone || 'UTC',
        symbol: apiData.symbol || null,
        dailyHours: Number(apiData.daily_hours) || 8,
        workdays: apiData.workdays
          ? apiData.workdays.split(',')
          : INITIAL_COMPANY_SETTINGS.workdays,
      }
    }
    return INITIAL_COMPANY_SETTINGS
  }, [companyData])

  const formik = useFormik<CompanySettingsData>({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().required('Company name is required'),
      timezone: Yup.string().required('Timezone is required'),
      dailyHours: Yup.number()
        .required('Workday duration is required')
        .min(1, 'Minimum 1 hour')
        .max(24, 'Maximum 24 hours'),
      workdays: Yup.array()
        .min(1, 'At least one workday is required')
        .required('Workdays are required'),
    }),
    onSubmit: async values => {
      try {
        const formData = new FormData()
        formData.append('name', values.name)
        if (values.logo instanceof File) {
          formData.append('logo', values.logo)
        }
        formData.append('timezone', values.timezone)
        if (values.symbol) formData.append('symbol', values.symbol)
        formData.append('daily_hours', values.dailyHours.toString())
        formData.append('workdays', values.workdays.join(','))

        await updateCompany(formData).unwrap()
        toast.success('Company profile updated')
      } catch (error) {
        const message =
          (error as { data?: { message?: string } })?.data?.message ||
          'Update failed'
        toast.error(message)
      }
    },
  })

  if (isCompanyLoading) {
    return (
      <div className='animate-in fade-in duration-500 space-y-8'>
        {/* Identity Card Skeleton */}
        <div className='rounded-2xl border border-border bg-card p-8 space-y-10'>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-10'>
            <div className='space-y-4'>
              <Skeleton className='h-7 w-32 rounded-lg' />
              <Skeleton className='h-4 w-full rounded-md' />
            </div>
            <div className='md:col-span-2 space-y-6'>
              {[1, 2, 3].map(i => (
                <div key={i} className='space-y-3'>
                  <Skeleton className='h-4 w-28 rounded-md' />
                  <Skeleton className='h-12 w-full rounded-xl' />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Availability Card Skeleton */}
        <div className='rounded-2xl border border-border bg-card p-8 space-y-10'>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-10'>
            <div className='space-y-4'>
              <Skeleton className='h-7 w-32 rounded-lg' />
              <Skeleton className='h-4 w-full rounded-md' />
            </div>
            <div className='md:col-span-2 space-y-8'>
              <div className='space-y-4'>
                <Skeleton className='h-4 w-32 rounded-md' />
                <Skeleton className='h-12 w-24 rounded-xl' />
              </div>
            </div>
          </div>
        </div>

        <div className='flex justify-end pt-4'>
          <Skeleton className='h-12 w-44 rounded-full' />
        </div>
      </div>
    )
  }

  return (
    <div className='h-full flex flex-col animate-in fade-in slide-in-from-bottom-2 duration-500 overflow-hidden'>
      <div className='flex-1 overflow-auto pr-2 custom-scrollbar'>
        <form onSubmit={formik.handleSubmit} className='space-y-8'>
          {/* Identity Card */}
          <div className='rounded-2xl border border-border  overflow-hidden'>
            <div className='p-8 grid grid-cols-1 md:grid-cols-3 gap-10'>
              <div className='space-y-2'>
                <h3 className='text-lg font-bold text-foreground'>Identity</h3>
                <p className='text-xs text-muted-foreground leading-relaxed'>
                  Core information that identifies your organization.
                </p>
              </div>

              <div className='md:col-span-2 space-y-6'>
                <div className='space-y-8'>
                  <div className='space-y-3'>
                    <Label
                      htmlFor='name'
                      className='text-xs font-bold uppercase tracking-widest text-muted-foreground'
                    >
                      Company Name <span className='text-destructive'>*</span>
                    </Label>
                    <Input
                      id='name'
                      name='name'
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`h-12 rounded-xl border-border bg-card/50 transition-all focus-visible:ring-primary/20 ${
                        formik.touched.name && formik.errors.name
                          ? 'border-destructive'
                          : ''
                      }`}
                      placeholder='Enter company name'
                    />
                    {formik.touched.name && formik.errors.name && (
                      <p className='text-[10px] font-bold text-destructive uppercase'>
                        {formik.errors.name}
                      </p>
                    )}
                  </div>

                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-8'>
                    <div className='space-y-3'>
                      <Label className='text-xs font-bold uppercase tracking-widest text-muted-foreground'>
                        Timezone <span className='text-destructive'>*</span>
                      </Label>
                      <Select
                        value={formik.values.timezone}
                        onValueChange={(value: string) =>
                          formik.setFieldValue('timezone', value)
                        }
                      >
                        <SelectTrigger
                          className={`h-12 w-full rounded-xl border-border bg-card/50 px-4 text-sm transition-all shadow-none ${
                            formik.touched.timezone && formik.errors.timezone
                              ? 'border-destructive'
                              : ''
                          }`}
                        >
                          <SelectValue placeholder='Select timezone' />
                        </SelectTrigger>
                        <SelectContent className='rounded-xl border-border bg-card shadow-xl max-h-[350px]'>
                          <SelectItem value='America/Los_Angeles'>
                            PST (UTC−8) – Pacific Time
                          </SelectItem>
                          <SelectItem value='America/Denver'>
                            MST (UTC−7) – Mountain Time
                          </SelectItem>
                          <SelectItem value='America/Chicago'>
                            CST (UTC−6) – Central Time
                          </SelectItem>
                          <SelectItem value='America/New_York'>
                            EST (UTC−5) – Eastern Time
                          </SelectItem>
                          <SelectItem value='UTC'>UTC (Standard)</SelectItem>
                          <SelectItem value='Europe/London'>
                            GMT (UTC+0) – UK / W. Europe
                          </SelectItem>
                          <SelectItem value='Europe/Paris'>
                            CET (UTC+1) – Central Europe
                          </SelectItem>
                          <SelectItem value='Asia/Kolkata'>
                            IST (UTC+5:30) – India
                          </SelectItem>
                          <SelectItem value='Asia/Singapore'>
                            SGT (UTC+8) – Singapore / Malaysia
                          </SelectItem>
                          <SelectItem value='Asia/Tokyo'>
                            JST (UTC+9) – Japan
                          </SelectItem>
                          <SelectItem value='Australia/Sydney'>
                            AEST (UTC+10) – Australia (East)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      {formik.touched.timezone && formik.errors.timezone && (
                        <p className='text-[10px] font-bold text-destructive uppercase'>
                          {formik.errors.timezone}
                        </p>
                      )}
                    </div>
                    <div className='space-y-3'>
                      <Label className='text-xs font-bold uppercase tracking-widest text-muted-foreground'>
                        Currency
                      </Label>
                      <Select
                        value={formik.values.symbol || '$'}
                        onValueChange={(value: string) =>
                          formik.setFieldValue('symbol', value)
                        }
                      >
                        <SelectTrigger className='h-12 w-full rounded-xl border-border bg-card/50 px-4 text-sm transition-all shadow-none'>
                          <SelectValue placeholder='Select currency' />
                        </SelectTrigger>
                        <SelectContent className='rounded-xl border-border bg-card shadow-xl max-h-[300px]'>
                          <SelectItem value='$'>$ – US Dollar (USD)</SelectItem>
                          <SelectItem value='€'>€ – Euro (EUR)</SelectItem>
                          <SelectItem value='£'>
                            £ – British Pound Sterling (GBP)
                          </SelectItem>
                          <SelectItem value='₹'>
                            ₹ – Indian Rupee (INR)
                          </SelectItem>
                          <SelectItem value='¥'>
                            ¥ – Japanese Yen (JPY / CNY)
                          </SelectItem>
                          <SelectItem value='₩'>
                            ₩ – South Korean Won (KRW)
                          </SelectItem>
                          <SelectItem value='₽'>
                            ₽ – Russian Ruble (RUB)
                          </SelectItem>
                          <SelectItem value='A$'>
                            A$ – Australian Dollar (AUD)
                          </SelectItem>
                          <SelectItem value='C$'>
                            C$ – Canadian Dollar (CAD)
                          </SelectItem>
                          <SelectItem value='S$'>
                            S$ – Singapore Dollar (SGD)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className='pt-4'>
                  <div className='flex items-center gap-8'>
                    <div className='relative w-24 h-24 rounded-[2.5rem] bg-muted flex items-center justify-center overflow-hidden border border-border group/logo'>
                      {isCompanyLoading ? (
                        <Loader2 className='w-6 h-6 animate-spin text-muted-foreground/30' />
                      ) : formik.values.logo ? (
                        <img
                          src={
                            typeof formik.values.logo === 'string'
                              ? formik.values.logo
                              : URL.createObjectURL(formik.values.logo)
                          }
                          alt='Company Logo'
                          className='w-full h-full object-cover'
                        />
                      ) : (
                        <Upload className='w-6 h-6 text-muted-foreground/40' />
                      )}
                    </div>
                    <div className='space-y-3'>
                      <input
                        type='file'
                        ref={fileInputRef}
                        className='hidden'
                        accept='.jpg,.jpeg,.png'
                        onChange={e => {
                          const file = e.target.files?.[0]
                          if (file) {
                            if (file.size > 5 * 1024 * 1024) {
                              toast.error('File size exceeds 5MB limit')
                              return
                            }
                            formik.setFieldValue('logo', file)
                          }
                          if (fileInputRef.current) {
                            fileInputRef.current.value = ''
                          }
                        }}
                      />
                      <Button
                        type='button'
                        variant='outline'
                        onClick={() => fileInputRef.current?.click()}
                        className='h-9 px-6 rounded-full text-xs font-bold'
                      >
                        Upload Logo
                      </Button>
                      <p className='text-[10px] text-muted-foreground font-bold uppercase tracking-wide'>
                        JPG, PNG allowed (max 5mb)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Availability Card */}
          <div className='rounded-2xl border border-border  overflow-hidden'>
            <div className='p-8 grid grid-cols-1 md:grid-cols-3 gap-10'>
              <div className='space-y-2'>
                <h3 className='text-lg font-bold text-foreground'>
                  Availability
                </h3>
                <p className='text-xs text-muted-foreground leading-relaxed'>
                  Operational standards for reporting and metrics.
                </p>
              </div>

              <div className='md:col-span-2 space-y-10'>
                <div className='space-y-4'>
                  <Label className='text-xs font-bold uppercase tracking-widest text-muted-foreground'>
                    Daily Work Duration{' '}
                    <span className='text-destructive'>*</span>
                  </Label>
                  <div className='flex items-center gap-4'>
                    <Input
                      type='number'
                      name='dailyHours'
                      value={formik.values.dailyHours}
                      min={1}
                      max={24}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`h-12 w-24 rounded-xl border-border bg-card/50 text-center font-bold text-lg ${
                        formik.touched.dailyHours && formik.errors.dailyHours
                          ? 'border-destructive'
                          : ''
                      }`}
                    />
                    <span className='text-xs font-bold uppercase tracking-tight text-muted-foreground'>
                      Hours per day
                    </span>
                  </div>
                  {formik.touched.dailyHours && formik.errors.dailyHours && (
                    <p className='text-[10px] font-bold text-destructive uppercase'>
                      {formik.errors.dailyHours}
                    </p>
                  )}
                </div>

                <div className='space-y-4'>
                  <Label className='text-xs font-bold uppercase tracking-widest text-muted-foreground'>
                    Active Workdays <span className='text-destructive'>*</span>
                  </Label>
                  <div className='flex flex-wrap gap-2.5'>
                    {DAYS_OF_WEEK.map(day => {
                      const fullName = FULL_DAY_NAMES[day]
                      const isActive = formik.values.workdays.includes(fullName)
                      return (
                        <button
                          key={day}
                          type='button'
                          onClick={async () => {
                            const newDays = isActive
                              ? formik.values.workdays.filter(
                                  d => d !== fullName
                                )
                              : [...formik.values.workdays, fullName]
                            await formik.setFieldValue(
                              'workdays',
                              newDays,
                              true
                            )
                            formik.setFieldTouched('workdays', true, true)
                          }}
                          className={`h-10 w-16 rounded-xl text-xs font-bold transition-all ${
                            isActive
                              ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                              : 'bg-card/50 border border-border text-foreground hover:bg-muted'
                          }`}
                        >
                          {day}
                        </button>
                      )
                    })}
                  </div>
                  {formik.touched.workdays && formik.errors.workdays && (
                    <p className='text-[10px] font-bold text-destructive uppercase'>
                      {typeof formik.errors.workdays === 'string'
                        ? formik.errors.workdays
                        : 'Required'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className='flex justify-end py-4'>
            <Button
              type='submit'
              disabled={isUpdatingCompany || !formik.isValid}
              className='h-11 px-10 rounded-full shadow-lg shadow-primary/20 transition-all active:scale-95 gap-3'
            >
              {isUpdatingCompany ? (
                <Loader2 className='w-4 h-4 animate-spin' />
              ) : (
                <Save className='w-4 h-4' />
              )}
              <span className='text-sm font-bold'>Save Settings</span>
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default OrganizationDetails
